/**
 * What a finding's severity looks like.
 *
 * There were two answers to this before, written independently: `FindingsList`
 * kept four hand-written Tailwind pairs (rose/amber/border/emerald, each with a
 * matching background at a different opacity), and `ScanHero` picked rose or
 * amber inline for the same three findings it repeats at the top of the page.
 * The pitch and the list could disagree about what "critical" looks like, and
 * neither of them agreed with a status pill three inches below.
 *
 * They agree here instead, and on the theme's own colours: a finding is a
 * verdict, which is what a status pill says everywhere else in this CRM. The
 * scanner keeps its own palette for two things where the colour carries scanner
 * meaning rather than a verdict - the score bands (`BAND_DOT`, `BAND_TEXT`) and
 * the version gaps (`GAP_CLASS`), both in `lib/siteScan`. Severity is not one of
 * them.
 */
import type { StatusTone } from "../../ui";
import type { FindingSeverity } from "../../../lib/siteScan";

export const SEVERITY_TONE: Record<FindingSeverity, StatusTone> = {
  critical: "lt",
  warning: "wt",
  info: "mut",
  good: "ok",
};

/**
 * The same four verdicts as a raw colour, for the places that are not a pill:
 * an icon in the pitch list, a rule down the side of a finding row.
 *
 * `info` is deliberately the muted foreground and not a colour. A finding we
 * merely noticed must not read as a finding we are worried about - a list where
 * every row is coloured is a list where nothing stands out.
 */
export const SEVERITY_COLOUR: Record<FindingSeverity, string> = {
  critical: "var(--color-destructive)",
  warning: "var(--color-warning)",
  info: "var(--color-muted-foreground)",
  good: "var(--color-success)",
};
